<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiDialog,
  UiSelect,
  UiTextarea,
  UiText,
  UiIcon,
  UiButton,
  UiInfoBox,
} from "@shopexperts/shared";
import { useAdminExpertLevels, useAssignAdminExpertLevel } from "@/app/composables/expert-levels";
import { formatCommission, formatThreshold } from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  expertId: number;
  organizationId?: number | null;
  expertName?: string | null;
  /** The level the expert currently sits at, used to preselect the dropdown. */
  currentLevelId?: number | null;
}>();

const emit = defineEmits<{
  (e: "assigned"): void;
}>();

const open = defineModel<boolean>({ default: false });

const { levels, isLoading: isLoadingLevels } = useAdminExpertLevels({
  isActive: true,
  enabled: open,
});
const { mutateAsync: assignLevel, isPending } = useAssignAdminExpertLevel();

const selectedLevelId = ref<string>("");
const notes = ref("");
const errorMessage = ref<string | null>(null);

const levelOptions = computed(() =>
  levels.value.map((level) => ({
    label: `${level.public_title} · ${formatCommission(level.commission_rate_bps)} commission`,
    value: String(level.id),
  })),
);

const selectedLevel = computed(
  () => levels.value.find((level) => String(level.id) === selectedLevelId.value) ?? null,
);

watch(open, (isOpen) => {
  if (isOpen) {
    selectedLevelId.value = props.currentLevelId ? String(props.currentLevelId) : "";
    notes.value = "";
    errorMessage.value = null;
  }
});

async function submit() {
  errorMessage.value = null;
  const levelId = Number(selectedLevelId.value);
  if (!Number.isFinite(levelId) || levelId <= 0) {
    errorMessage.value = "Select a level to assign.";
    return;
  }
  if (notes.value.trim().length === 0) {
    errorMessage.value = "Add a short reason — assignments are recorded for the audit trail.";
    return;
  }

  try {
    await assignLevel({
      expertId: props.expertId,
      organizationId: props.organizationId,
      body: { expert_level_id: levelId, notes: notes.value.trim() },
    });
    open.value = false;
    emit("assigned");
  } catch {
    // The composable surfaces a toast on error; keep the dialog open to retry.
  }
}

function cancel() {
  open.value = false;
}
</script>

<template>
  <UiDialog
    v-model="open"
    v-model:loading="isPending"
    title="Reassign expert level"
    size="lg"
  >
    <div class="flex flex-col gap-5">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        :text="expertName
          ? `Manually set ${expertName}'s level. This overrides auto-enrollment until their gross sales cross a higher threshold.`
          : 'Manually set this expert\'s level. This overrides auto-enrollment until their gross sales cross a higher threshold.'"
      />

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Level" />
        <UiSelect
          v-model="selectedLevelId"
          :options="levelOptions"
          :placeholder="isLoadingLevels ? 'Loading levels…' : 'Select a level'"
          :disabled="isLoadingLevels || isPending"
          size="md"
        />
      </div>

      <div
        v-if="selectedLevel"
        class="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-xl border border-se-gray-200 bg-se-gray-50 px-4 py-3"
      >
        <div class="flex items-center gap-1.5">
          <UiIcon name="dollar-circle" class="h-4 w-4 shrink-0 text-se-green" />
          <UiText
            as="span"
            variant="p-small"
            class="text-se-dark"
            :text="`${formatCommission(selectedLevel.commission_rate_bps)} commission`"
          />
        </div>
        <div class="flex items-center gap-1.5">
          <UiIcon name="sparkle" class="h-4 w-4 shrink-0 text-se-gray-500" />
          <UiText
            as="span"
            variant="p-small"
            class="text-se-gray-600"
            :text="`Auto-enrolls at ${formatThreshold(selectedLevel.auto_enroll_threshold_gross_cents)} gross`"
          />
        </div>
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Reason / notes" />
        <UiTextarea
          v-model="notes"
          :rows="3"
          placeholder="Why is this level being assigned? (kept on the assignment record)"
          :disabled="isPending"
        />
      </div>

      <UiInfoBox variant="info">
        <UiText as="span" variant="p-small">
          <span>
            Level assignments are append-only and audited — this records you as the assigner with
            source "admin". Gross-sales thresholds can still auto-promote the expert to a higher
            level later.
          </span>
        </UiText>
      </UiInfoBox>

      <UiText
        v-if="errorMessage"
        as="p"
        variant="p-small"
        class="text-red-600"
        :text="errorMessage"
      />
    </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <UiButton variant="outline" label="Cancel" :disabled="isPending" @click="cancel" />
        <UiButton
          variant="primary"
          :label="isPending ? 'Assigning…' : 'Assign level'"
          :disabled="isPending"
          @click="submit"
        />
      </div>
    </template>
  </UiDialog>
</template>
